import { useTranslation } from "react-i18next";
import { AppCard } from "@/components/ui";
import { Link, Cpu, Rocket } from "lucide-react";

const STEP_ICONS = [Link, Cpu, Rocket];
const STEP_COLORS = [
  "bg-rose/10 text-rose",
  "bg-accent/10 text-accent",
  "bg-success/10 text-success",
];

/**
 * How It Works section — 3-step process with luxury card design.
 * Each step has an icon, step number, title, and description.
 * Rosé Pine Dawn aesthetic with large rounded corners and hover effects.
 */
export function HowItWorksSection() {
  const { t } = useTranslation();

  const steps = t("landing.howItWorks.steps", {
    returnObjects: true,
  }) as {
    number: string;
    title: string;
    description: string;
  }[];

  return (
    <section id="how-it-works" className="py-32 px-6 scroll-mt-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-24 space-y-4">
          <h2 className="text-sm font-bold text-success uppercase tracking-[0.4em]">
            {t("landing.howItWorks.subtitle")}
          </h2>
          <h3 className="text-5xl font-bold">
            {t("landing.howItWorks.heading")}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent -translate-y-1/2 -z-10" />

          {steps.map((step, i) => {
            const Icon = STEP_ICONS[i] ?? Rocket;
            return (
              <AppCard
                key={step.number}
                className="bg-surface p-10 rounded-[3rem] border border-border shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all group"
              >
                <div
                  className={`w-16 h-16 rounded-[1.5rem] ${STEP_COLORS[i]} flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}
                >
                  <Icon className="w-8 h-8" />
                </div>
                <p className="text-[10px] font-black uppercase tracking-[0.5em] text-foreground-muted mb-4">
                  {step.number} // Sync
                </p>
                <h4 className="text-2xl font-bold mb-4">{step.title}</h4>
                <p className="text-foreground-secondary text-sm leading-relaxed font-light">
                  {step.description}
                </p>
              </AppCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
